// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * NodeMenu — SolyticsMenus integration for SolyticsFlow nodes.
 *
 * Renders an invisible wrapper that opens a SolyticsMenu on right-click
 * (context menu). The menu items follow the exact same shape as
 * SolyticsMenus' `items` prop:
 *
 *   data.menuItems = [
 *     { id: 'edit', content: 'Edit', icon: EditIcon, onClick: () => … },
 *     { id: 'delete', content: 'Delete', icon: DeleteIcon, onClick: () => … },
 *   ]
 *
 * If `data.menuItems` is falsy or empty → no context-menu behaviour is added;
 * children are rendered bare.
 *
 * When `parentId` is provided, a "Detach from group" item is automatically
 * prepended to the menu, allowing the user to detach the node via right-click.
 */
import React, { memo, useState, useCallback, useMemo } from 'react';
import { useReactFlow } from 'reactflow';
import { useTheme } from '@mui/material/styles';
import SolyticsMenus from '../../../SolyticsMenus';
import SolyticsBox from '../../../SolyticsBox';
import DetachButton from './DetachButton';
import { detachNodeFromGroup } from './useGroupNodeAdoption';

const NodeMenu = memo(
  ({ menuItems, nodeId, parentId, onNodeOrphaned, children }) => {
    const [anchorPosition, setAnchorPosition] = useState(null);
    const { getNodes, setNodes } = useReactFlow();
    const theme = useTheme();

    const handleContextMenu = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setAnchorPosition({ top: e.clientY, left: e.clientX });
    }, []);

    const handleClose = useCallback(() => {
      setAnchorPosition(null);
    }, []);

    const handleDetach = useCallback(() => {
      const currentNodes = getNodes();
      detachNodeFromGroup(nodeId, setNodes, currentNodes, onNodeOrphaned);
      handleClose();
    }, [nodeId, setNodes, getNodes, onNodeOrphaned, handleClose]);

    // Auto-add detach menu item when node is a child of a group
    const finalMenuItems = useMemo(() => {
      const base = menuItems || [];
      if (!parentId) return base;

      const detachItem = {
        id: '__detach__',
        content: 'Detach from group',
        onClick: handleDetach,
      };

      return [detachItem, ...base];
    }, [menuItems, parentId, handleDetach]);

    if (finalMenuItems.length === 0) return children;

    return (
      <>
        <SolyticsBox
          onContextMenu={handleContextMenu}
          position="relative"
          display={parentId ? 'inline-flex' : 'contents'}
          sx={{
            '&:hover .solytics-flow-detach': {
              opacity: 1,
              visibility: 'visible',
              transition: theme.transitions.create(['opacity', 'visibility']),
            },
            '&:focus-within .solytics-flow-detach': {
              opacity: 1,
              visibility: 'visible',
              transition: theme.transitions.create(['opacity', 'visibility']),
            },
          }}
        >
          {children}
          {parentId && (
            <DetachButton nodeId={nodeId} onNodeOrphaned={onNodeOrphaned} />
          )}
        </SolyticsBox>

        {/* Stop React portal event bubbling so menu interactions don't select the node */}
        <SolyticsBox
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <SolyticsMenus
            id={`node-menu-${nodeId}`}
            open={Boolean(anchorPosition)}
            onClose={handleClose}
            items={finalMenuItems}
            anchorReference="anchorPosition"
            anchorPosition={anchorPosition ?? { top: 0, left: 0 }}
          />
        </SolyticsBox>
      </>
    );
  }
);
NodeMenu.displayName = 'NodeMenu';

export default NodeMenu;
